/**
 * Takeover: what a non-motorized control does when it disagrees with the param — after a preset load, or after the mouse moved it.
 *
 * Works in control space (0…1) on both sides: the caller maps the param's current value back through the binding with `fromDomain`.
 *
 * - `jump`   — the param follows the control at once.
 * - `pickup` — ignored until the control CROSSES the param's value. Compares the previous and new positions, because a fast move skips values and an equality test would never fire.
 * - `scale`  — the param moves toward the control's end proportionally, so both arrive together, then follows.
 *
 * `caught` is dropped when the param moves away from what this control last wrote — something else (a preset, the mouse, a peer) took it.
 */

// Half of one 7-bit step: closer than this, control and param agree.
export const TAKEOVER_EPS = 1 / 254

export const createTakeover = () => ({ prev: undefined, caught: false, last: undefined })

/**
 * @param mode     'jump' | 'pickup' | 'scale'
 * @param st       state from `createTakeover`, mutated
 * @param hw       the control's new position
 * @param current  the param's value as a control position
 * @returns the position to write, or undefined while not caught
 */
export const takeover = (mode, st, hw, current) => {
	if (st.last !== undefined && Math.abs(current - st.last) > TAKEOVER_EPS) st.caught = false
	const prev = st.prev
	st.prev = hw
	if (mode === 'jump' || st.caught || Math.abs(hw - current) <= TAKEOVER_EPS) {
		st.caught = true
		return hw
	}
	if (prev === undefined || prev === hw) return undefined
	// A crossing catches in both modes.
	if ((prev - current) * (hw - current) <= 0) {
		st.caught = true
		return hw
	}
	if (mode !== 'scale') return undefined
	const up = hw > prev
	const room = up ? 1 - prev : prev
	if (room <= 0) {
		st.caught = true
		return hw
	}
	// The param keeps the share of the remaining travel it had: behind the control it moves faster, ahead of it slower, and the two meet at the end.
	const out = current + (hw - prev) * ((up ? 1 - current : current) / room)
	// Met: close enough, or the param ended up on the control's other side.
	if (Math.abs(out - hw) <= TAKEOVER_EPS || Math.sign(out - hw) !== Math.sign(current - prev)) {
		st.caught = true
		return hw
	}
	return out
}

/** Record what was written, as a control position, so the next message can tell whether something else moved the param. */
export const noteWritten = (st, position) => {
	st.last = position
}
